import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, Card, Row, Col, Button } from "react-bootstrap";
import { toast } from "react-toastify";

import PageTitle from "../../components/pageTitle";
import ResourceAllocationForm from "./ResourceAllocationForm";
import ResourceAllocationList from "./ResourceAllocationList";
import NoDataFound from "../../components/NoDataFound";
import { FaUsers } from "react-icons/fa";

import {
  fetchResourceAllocations,
  updateResourceAllocationData,
  deleteResourceAllocationById,
  createResourceAllocation,
} from "../../store/features/resourceAllocation/resourceAllocationSlice";

import { fetchUserById } from "../../store/features/user/userSlice";

function ResourceAllocation() {
  const dispatch = useDispatch();
  const { resourceAllocations, loading } = useSelector(
    (state) => state.resourceAllocations
  );
  const { usersCreatedBy, users } = useSelector((state) => state.users);
  const { role, _id } = useSelector((state) => state.auth);

  const [editData, setEditData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchUserById(_id)).unwrap();
        await dispatch(fetchResourceAllocations()).unwrap();
      } catch {
        toast.error("Failed to load data");
      }
    };
    loadData();
  }, [dispatch, _id]);

  const onSave = async (values, resetForm) => {
    try {
      if (editData?._id) {
        await dispatch(
          updateResourceAllocationData({ ...values, id: editData._id })
        ).unwrap();
        toast.success("Resource Allocation updated!");
      } else {
        if (role === "assistant superadmin") {
          values.assistantSuperAdminId = usersCreatedBy?.[0]?._id;
        }
        await dispatch(createResourceAllocation(values)).unwrap();
        toast.success("Resource Allocation created!");
      }
      await dispatch(fetchResourceAllocations());
      resetForm();
      setEditData(null);
    } catch {
      toast.error("Error saving Resource Allocation");
    }
  };

  const onDelete = async (ids) => {
    if (ids?.length > 0) {
      for (const id of ids) {
        try {
          await dispatch(deleteResourceAllocationById(id)).unwrap();
          toast.info(`Resource Allocation ${id} deleted`);
        } catch (error) {
          toast.error(`Error deleting Resource Allocation ${id}`);
        }
      }
      // Fetch all data after deletions
      await dispatch(fetchResourceAllocations());
    }
  };

  if (loading || !usersCreatedBy || usersCreatedBy.length === 0) {
    return <Spinner animation="border" />;
  }

  return (
    <>
      <PageTitle
        pageTitle="Resource Allocation"
        pageSubtitle="Powering Strategic Resource Management."
      />

      <Card className="mb-3">
        <Card.Body>
          <ResourceAllocationForm
            role={role}
            usersCreatedBy={usersCreatedBy}
            resourceAllocations={resourceAllocations}
            editData={editData}
            setEditData={setEditData}
            onSave={onSave}
            users={users}
          />
        </Card.Body>
      </Card>
      {resourceAllocations.length > 0 ? (
        <ResourceAllocationList
          resourceAllocations={resourceAllocations}
          onEdit={setEditData}
          onDelete={onDelete}
        />
      ) : (
        <NoDataFound
          icon={<FaUsers size={50} />}
          title="Resource Allocations not found"
          description="Try to add some resource allocations."
        />
      )}
    </>
  );
}

export default ResourceAllocation;
